/**
 * Question Validation Utilities
 * 
 * This module provides validation logic for form questions with complex rule-based constraints.
 * It implements IF/THEN logic to validate user answers against defined rules.
 */

/**
 * Validates a question's answers against its validation rules
 * 
 * @param {Object} question - The complete question object including validation rules
 * @param {Array} userAnswerIds - Array of selected answer IDs by the user
 * @returns {Object} Validation result with success flag and error details
 */
export const validateQuestion = (question, userAnswerIds = []) => {
  // If no validation rules are defined, consider it valid
  if (!question.validation || !Array.isArray(question.validation) || question.validation.length === 0) {
    return { success: true, errors: [] };
  }

  // Only validate multiple choice questions
  if (question.questionType !== 'multiple' && question.questionType !== 'pdca') {
    return { success: true, errors: [] };
  }

  const errors = [];
  const userAnswerSet = new Set(userAnswerIds);

  // Evaluate each validation rule
  for (const rule of question.validation) {
    const {
      source_answers = [],
      source_condition,
      dest_answers = [],
      dest_condition
    } = rule;

    // Skip invalid rules
    if (!source_answers.length || !dest_answers.length || !source_condition || !dest_condition) {
      continue;
    }

    // Check if the source condition is triggered
    const isSourceTriggered = evaluateSourceCondition(source_answers, source_condition, userAnswerSet);

    if (isSourceTriggered) {
      // Evaluate the destination condition
      const destValidation = evaluateDestinationCondition(dest_answers, dest_condition, userAnswerSet);

      if (!destValidation.isValid) {
        errors.push({
          source_trigger: source_answers.filter(id => userAnswerSet.has(id)),
          dest_trigger: destValidation.violatingAnswers,
          dest_condition: dest_condition,
          rule_description: getErrorMessage(rule, question.answers)
        });
      }
    }
  }

  return {
    success: errors.length === 0,
    errors: errors
  };
};

/**
 * Evaluates if the source condition is met based on user selections
 * 
 * @param {Array} sourceAnswers - Array of source answer IDs
 * @param {String} sourceCondition - Condition type (any_of, all, not_in)
 * @param {Set} userAnswerSet - Set of user-selected answer IDs
 * @returns {Boolean} Whether the source condition is triggered
 */
const evaluateSourceCondition = (sourceAnswers, sourceCondition, userAnswerSet) => {
  switch (sourceCondition) {
    case 'any_of':
      // Triggered if at least one source answer is selected
      return sourceAnswers.some(answerId => userAnswerSet.has(answerId));
    
    case 'all':
      // Triggered if all source answers are selected
      return sourceAnswers.every(answerId => userAnswerSet.has(answerId));
    
    case 'not_in':
      // Triggered if none of the source answers are selected
      return !sourceAnswers.some(answerId => userAnswerSet.has(answerId));
    
    default:
      return false;
  }
};

/**
 * Evaluates if the destination condition is satisfied
 * 
 * @param {Array} destAnswers - Array of destination answer IDs
 * @param {String} destCondition - Destination condition type
 * @param {Set} userAnswerSet - Set of user-selected answer IDs
 * @returns {Object} Validation result with validity flag and violating answers
 */
const evaluateDestinationCondition = (destAnswers, destCondition, userAnswerSet) => {
  switch (destCondition) {
    case 'mandatory_any':
      // At least one destination answer must be selected
      const hasAny = destAnswers.some(answerId => userAnswerSet.has(answerId));
      return {
        isValid: hasAny,
        violatingAnswers: hasAny ? [] : destAnswers // All are missing if none selected
      };
    
    case 'mandatory_all':
      // All destination answers must be selected
      const missingAnswers = destAnswers.filter(answerId => !userAnswerSet.has(answerId));
      return {
        isValid: missingAnswers.length === 0,
        violatingAnswers: missingAnswers
      };
    
    case 'excluded_all':
      // None of the destination answers should be selected
      const selectedExcluded = destAnswers.filter(answerId => userAnswerSet.has(answerId));
      return {
        isValid: selectedExcluded.length === 0,
        violatingAnswers: selectedExcluded
      };
    
    default:
      return { isValid: true, violatingAnswers: [] };
  }
};

/**
 * Generates a human-readable error message for a validation rule
 * 
 * @param {Object} rule - The validation rule that failed
 * @param {Array} allAnswers - All available answers for the question
 * @returns {String} Human-readable error message
 */
const getErrorMessage = (rule, allAnswers) => {
  const getAnswerText = (answerId) => {
    const answer = allAnswers.find(a => (a.answerId || a.id) === answerId);
    return answer?.answerText?.[0]?.text || answer?.answerText || `Answer ${answerId}`;
  };

  const sourceTexts = rule.source_answers.map(getAnswerText).join(', ');
  const destTexts = rule.dest_answers.map(getAnswerText).join(', ');

  const sourceConditionText = {
    'any_of': 'almeno una tra',
    'all': 'tutte le opzioni',
    'not_in': 'nessuna delle opzioni'
  }[rule.source_condition] || rule.source_condition;

  const destConditionText = {
    'mandatory_any': 'è necessario selezionare almeno una tra',
    'mandatory_all': 'è necessario selezionare tutte le opzioni',
    'excluded_all': 'non è possibile selezionare'
  }[rule.dest_condition] || rule.dest_condition;

  return `Se ${sourceConditionText}: "${sourceTexts}", allora ${destConditionText}: "${destTexts}".`;
};

/**
 * Gets all answers that should be disabled based on current selections and validation rules
 * 
 * @param {Object} question - The question object with validation rules
 * @param {Array} userAnswerIds - Current user selections
 * @returns {Array} Array of answer IDs that should be disabled
 */
export const getDisabledAnswers = (question, userAnswerIds = []) => {
  if (!question.validation || question.questionType !== 'multiple') {
    return [];
  }

  const disabledAnswers = new Set();
  const userAnswerSet = new Set(userAnswerIds);

  for (const rule of question.validation) {
    const {
      source_answers = [],
      source_condition,
      dest_answers = [],
      dest_condition
    } = rule;

    // Check if source condition is triggered
    const isSourceTriggered = evaluateSourceCondition(source_answers, source_condition, userAnswerSet);

    if (isSourceTriggered && dest_condition === 'excluded_all') {
      // Add excluded answers to disabled set
      dest_answers.forEach(answerId => disabledAnswers.add(answerId));
    }
  }

  return Array.from(disabledAnswers);
};

/**
 * Formats validation errors for display in the UI
 * 
 * @param {Array} errors - Array of validation errors
 * @returns {Array} Array of formatted error messages
 */
export const formatValidationErrors = (errors) => {
  return errors.map(error => ({
    message: error.rule_description,
    type: 'validation',
    severity: 'error'
  }));
};
